import React, { useCallback, useMemo } from 'react'

import { useTranslation } from '@common/config/localization'
import { isMobileBrowser } from '@common/config/env'
import NetworkFilterDropdown, {
  NetworkFilterOption
} from '@common/modules/dashboard/home-kit/NetworkFilterDropdown'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import { sortNetworksPulseFirst } from '@common/utils/sortNetworks'
import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { getUiType } from '@web/utils/uiType'

const { isPopup } = getUiType()

interface Props {
  currentTab: TabType
}

const SelectNetwork = ({ currentTab }: Props) => {
  const { t } = useTranslation()
  const { mode } = useAppMode()
  const { dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { networks } = useExtensionAwareNetworks()
  const { dispatch } = useBackgroundService()

  // On mobile-device browsers, private mode scans one chain at a time (memory),
  // so the "All Networks" option is omitted — see railgunContext clamping.
  const hideAllNetworks = isMobileBrowser && mode === ModeEnum.PRIVATE

  const options: NetworkFilterOption[] = useMemo(
    () => [
      ...(hideAllNetworks ? [] : [{ value: 'all', label: t('All Networks') }]),
      ...sortNetworksPulseFirst(networks).map((n) => ({
        value: n.chainId.toString(),
        label: n.name,
        iconId: n.chainId.toString()
      }))
    ],
    [networks, t, hideAllNetworks]
  )

  const value = dashboardNetworkFilter ? dashboardNetworkFilter.toString() : 'all'

  const onSelect = useCallback(
    (next: string) => {
      const network = networks.find((n) => n.chainId.toString() === next)
      dispatch({
        type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
        params: { dashboardNetworkFilter: network ? network.chainId : null }
      })
    },
    [networks, dispatch]
  )

  return (
    <NetworkFilterDropdown
      testID={`networks-dropdown-${currentTab}`}
      options={options}
      value={value}
      onSelect={onSelect}
      compact={isPopup}
      minWidth={isPopup ? 140 : 190}
    />
  )
}

export default React.memo(SelectNetwork)
